import { useState, useEffect } from 'react';
import { getConfig } from '../utils/runtimeConfig';

// Default pricing fallbacks (in case API fails)
const DEFAULT_BOOST_TIERS = {
  standard: {
    name: "Standard Boost",
    price: 2999,
    duration: "7 days",
    multiplier: "1.5x",
    features: [
      "1.5x visibility in search results",
      "Featured in category listings",
      "Priority in nearby searches",
      "Basic analytics dashboard"
    ]
  },
  premium: {
    name: "Premium Boost",
    price: 5999,
    duration: "14 days",
    multiplier: "2.5x",
    popular: true,
    features: [
      "2.5x visibility in search results",
      "Featured on homepage carousel",
      "Priority in all location searches",
      "Social media promotion",
      "Advanced analytics dashboard",
      "Dedicated support"
    ]
  },
  elite: {
    name: "Elite Boost",
    price: 9999,
    duration: "30 days",
    multiplier: "4x",
    features: [
      "4x visibility in search results",
      "Premium homepage placement",
      "Featured in WhatsApp broadcasts",
      "Email campaign inclusion",
      "Professional video tour option",
      "Full analytics suite",
      "Priority 24/7 support"
    ]
  }
};

const DEFAULT_ADDONS = {
  photography: {
    id: "photography",
    name: "Professional Photography",
    price: 2999,
    description: "HD photos by professional photographer",
    features: [
      "Up to 20 HD photos",
      "Wide-angle shots",
      "Photo editing included"
    ]
  },
  virtual_tour: {
    id: "virtual_tour",
    name: "360° Virtual Tour",
    price: 4999,
    description: "Interactive virtual property tour",
    features: [
      "360° panoramic views",
      "Interactive navigation",
      "Mobile-friendly viewer"
    ]
  },
  drone_video: {
    id: "drone_video",
    name: "Drone Video",
    price: 7999,
    description: "Aerial video of your property",
    features: [
      "Professional drone footage",
      "Edited video (2-3 min)",
      "Music and transitions"
    ]
  },
  legal_verification: {
    id: "legal_verification",
    name: "Legal Verification",
    price: 1999,
    description: "Property document verification",
    features: [
      "Title deed verification",
      "Encumbrance check",
      "Legal opinion report"
    ]
  }
};

export const useAddonPricing = () => {
  const [boostTiers, setBoostTiers] = useState(DEFAULT_BOOST_TIERS);
  const [addons, setAddons] = useState(DEFAULT_ADDONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const { api_base } = getConfig();
        const response = await fetch(`${api_base}/addons/pricing/all`);
        if (response.ok) {
          const data = await response.json();
          if (data.boost_tiers) setBoostTiers(data.boost_tiers);
          if (data.addons) setAddons(data.addons);
        }
      } catch (err) {
        console.warn('Using default addon pricing:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  return {
    boostTiers,
    addons,
    loading,
    error,
    // Helper to get photography price
    getPhotographyPrice: () => addons.photography?.price || DEFAULT_ADDONS.photography.price,
    // Helper to get boost tier price
    getBoostPrice: (tier) => boostTiers[tier]?.price || DEFAULT_BOOST_TIERS[tier]?.price || 0,
  };
};

export default useAddonPricing;
